import React, {Fragment, useCallback, useState} from "react";
import {Box, Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {HashLink} from "react-router-hash-link";
import AiSettingsDialog from "../components/ai-assistant-settings-dialog";
import AiAuthChangePasswordDialog from "../components/ai-assistant-settings-dialog/AiAuthChangePasswordDialog";
import CustomDialog from "../components/custom-dialog";
import CustomDialogTitle from "../components/custom-dialog-title";
import AiChatIcon from "../components/custom-icons/AiChatIcon";
import Footer from "../components/footer";
import Header from "../components/header";
import MaterialTable from "../components/material-table";
import Page from "../components/page";
import PasswordInput from "../components/password-input";
import {getHomeInternalUrl} from "../constants/appRoutes";
import Call from "../hocs/call";
import {useHasFeature} from "../hooks/useHasFeature";
import useLanguages from "../hooks/useLanguages";
import {NodeVisibility} from "../model/IHubMinimalNode.d.ts";
import {
  fetchNodes,
  requestAiSettingsAuthorization,
  showNodeAISettingsModal
} from "../state/ai-assistant/aiAssistantAction";
import {aiNodeSettingsSelector} from "../state/ai-assistant/aiAssistantSelector";
import {LICENSE_FEATURES_AI} from "../state/app/contants";
import {externalServicesSelector} from "../state/config/configSelectors";
import {hubSelector} from "../state/hub/hubSelectors";
import {showUserLoginForm} from "../state/user/userActions";
import {userSelector} from "../state/user/userSelectors";
import {localizeI18nObj} from "../utils/i18n";
import {getPageTitle} from "../utils/other";

const AiConfig = () => {
  const {t, i18n} = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {defaultLanguage: language, languages} = useLanguages();

  const hub = useSelector(hubSelector);
  const aiBaseUrl = useSelector(externalServicesSelector)?.ai;

  const user = useSelector(userSelector);
  const {authToken, nodes} = useSelector(aiNodeSettingsSelector);

  const [password, setPassword] = useState("");

  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);

  const handleLoginShow = useCallback(() => dispatch(showUserLoginForm(undefined, undefined, true)), [dispatch]);
  const handleFetchNodes = useCallback(() => dispatch(fetchNodes()), [dispatch]);
  const handleAuthorizationSubmit = useCallback(
    evt => {
      evt?.preventDefault();

      if (!password) {
        return;
      }

      dispatch(requestAiSettingsAuthorization(password, aiBaseUrl));
    },
    [dispatch, password, aiBaseUrl]
  );

  const isAiInLicense = useHasFeature(LICENSE_FEATURES_AI);
  if (!isAiInLicense) {
    navigate(getHomeInternalUrl());
    return null;
  }

  return (
    <Fragment>
      <HashLink to={{hash: "#main", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.main")}
      </HashLink>
      <HashLink to={{hash: "#footer", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.footer")}
      </HashLink>
      <Page title={getPageTitle([t("pages.aiConfig.title"), hub?.hub?.name], t)}>
        <Header />
        <Box
          component="main"
          id="main"
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            "& #footer": {
              paddingTop: "0px"
            }
          }}
        >
          <Call cb={handleLoginShow} disabled={user.isAuthenticated}>
            {user.isAuthenticated && (
              <Box
                sx={{
                  padding: "16px",
                  width: "100%",
                  height: "100%"
                }}
              >
                <CustomDialog maxWidth="sm" open={!authToken}>
                  <CustomDialogTitle onClose={() => navigate(getHomeInternalUrl())}>
                    {t("pages.aiConfig.loginDialog.title")}
                  </CustomDialogTitle>
                  <Box component="form" noValidate onSubmit={handleAuthorizationSubmit}>
                    <DialogContent>
                      <PasswordInput
                        disableAutocomplete
                        id="ai-config__password-input"
                        label={t("pages.aiConfig.loginDialog.form.password.label")}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginTop: "8px"
                        }}
                        value={password}
                        onChange={({target}) => setPassword(target.value)}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button type="button" onClick={() => setChangePasswordDialogOpen(true)}>
                        {t("pages.aiConfig.loginDialog.form.changePassword.label")}
                      </Button>
                      <Button type="submit" disabled={(password || "").length === 0}>
                        {t("commons.confirm.confirm")}
                      </Button>
                    </DialogActions>
                  </Box>
                </CustomDialog>
                {authToken && (
                  <Call cb={handleFetchNodes} disabled={nodes !== null}>
                    <MaterialTable
                      key="nodeId"
                      columns={[
                        {
                          field: "code",
                          title: t("scenes.nodesSettings.table.columns.nodeCode")
                        },
                        {
                          field: "title",
                          title: t("scenes.nodesSettings.table.columns.nodeName"),
                          render: ({title}) => localizeI18nObj(title, language, languages)
                        },
                        {
                          field: "type",
                          title: t("scenes.nodesSettings.table.columns.nodeType"),
                          render: ({type}) =>
                            i18n.exists(`commons.node.types.${type}`) ? t(`commons.node.types.${type}`) : type
                        },
                        {
                          field: "active",
                          title: t("scenes.nodesSettings.table.columns.isNodeActive.title"),
                          render: ({active}) =>
                            active
                              ? t("scenes.nodesSettings.table.columns.isNodeActive.values.true")
                              : t("scenes.nodesSettings.table.columns.isNodeActive.values.false")
                        },
                        {
                          field: "visible",
                          title: t("scenes.nodesSettings.table.columns.isNodeVisible.title"),
                          render: ({visible}) =>
                            visible === NodeVisibility.Yes
                              ? t("scenes.nodesSettings.table.columns.isNodeVisible.values.true")
                              : t("scenes.nodesSettings.table.columns.isNodeVisible.values.false")
                        },
                        {
                          field: "default",
                          title: t("scenes.nodesSettings.table.columns.isNodeDefault.title"),
                          render: node =>
                            node.default
                              ? t("scenes.nodesSettings.table.columns.isNodeDefault.values.true")
                              : t("scenes.nodesSettings.table.columns.isNodeDefault.values.false")
                        }
                      ]}
                      data={nodes}
                      actions={[
                        ({nodeId}) => ({
                          tooltip: t("pages.aiConfig.table.actions.aiSettings"),
                          icon: <AiChatIcon />,
                          onClick: () => dispatch(showNodeAISettingsModal(nodeId))
                        })
                      ]}
                    />
                  </Call>
                )}
              </Box>
            )}
          </Call>

          <AiSettingsDialog />

          <AiAuthChangePasswordDialog
            open={changePasswordDialogOpen}
            onClose={() => setChangePasswordDialogOpen(false)}
          />

          <Footer />
        </Box>
      </Page>
    </Fragment>
  );
};

export default AiConfig;
